<template>
  <div>
    <h4 class="mb-4">Solicitação de Recurso</h4>
    <v-card rounded="lg" class="pa-2 mb-4" v-if="hasPermission(8388608)">
      <v-btn-toggle
        v-model="ownerType"
        class="w-full"
        mandatory
        color="primary"
      >
        <v-btn value="me" small class="flex-1">Para mim</v-btn>
        <v-btn value="third" small class="flex-1">Para terceiro</v-btn>
      </v-btn-toggle>
      <member-selector
        v-show="ownerType === 'third'"
        v-model="owner"
        class="mt-2"
      />
    </v-card>

    <v-card rounded="lg" class="pa-2 mb-4">
      <party-selector v-show="!party" v-model="party" />
      <div
        v-if="party"
        class="d-flex flex-column flex-md-row gap-4 align-center"
      >
        <div>
          <v-img
            :src="party.cover"
            width="135px"
            class="rounded-lg"
            :aspect-ratio="16 / 9"
          />
        </div>
        <div class="flex-grow-1">
          <span class="d-block mb-0 text-overline lh-1">
            {{ party.date | date }} - {{ party.endDate | date }}
          </span>
          <h6 class="mb-0">{{ party.name }}</h6>
          <span class="d-block" v-if="party.Address">
            {{ party.Address.city }}/{{ party.Address.state }}
          </span>
        </div>
        <div class="align-self-start">
          <v-btn icon @click="party = null">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
    <div>
      <v-expansion-panels v-model="activePanel" focusable>
        <v-expansion-panel v-for="(item, key) in itens" :key="key">
          <v-expansion-panel-header>
            <div class="flex-grow-0">
              <v-icon left>{{ types[item.type].icon }}</v-icon>
              <b>{{ types[item.type].name }}</b>
            </div>
            <v-spacer />
            <div class="flex-grow-0 d-flex align-center gap-">
              <template v-if="item.valid">
                <h6 class="mb-0">{{ item.amount | currency(true) }}</h6>
                <v-icon color="green" left right>mdi-check</v-icon>
              </template>
              <span v-else>
                <v-icon color="red" left right>mdi-close</v-icon>
              </span>
              <v-scroll-y-reverse-transition>
                <v-btn
                  v-if="activePanel === key"
                  icon
                  @click.stop="removeItem(key)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-scroll-y-reverse-transition>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-3">
            <component
              :is="types[item.type].component"
              :value="item.data"
              @update="updateItem(key, $event)"
              @update:valid="updateValid(key, $event)"
              @update:amount="updateTotal(key, $event)"
              :readonly="!!loading"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <v-menu>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          block
          class="mt-4"
          text
          :disabled="!!loading"
        >
          Adicionar Item
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="item in types"
          :key="item.id"
          @click="addItem(item.id)"
        >
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </v-menu>

    <h5 class="text-end">Total: {{ total | currency(true) }}</h5>

    <v-alert v-if="error" type="error" class="mt-4">
      {{ error }}
    </v-alert>

    <div class="d-flex justify-end mt-4 gap-4">
      <v-btn
        color="primary"
        :disabled="!isValid"
        :loading="loading === 'draft'"
        @click="saveDraft"
      >
        Salvar rascunho
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import RESOURCE_REQUEST from "@/services/admin/resourceRequest";
import PartySelector from "@/components/global/selector/PartySelector.vue";
import MemberSelector from "@/components/global/selector/MemberSelector.vue";

const context = require.context(
  "../../components/admin/resourceRequest/types",
  true,
  /\.vue$/
);

const types = context.keys().reduce((acc, key) => {
  const { manifest, default: component } = context(key);
  acc[manifest.id] = {
    ...manifest,
    component,
  };
  return acc;
}, {});

export default {
  name: "ResourceRequestCreate",
  metaInfo: {
    title: "Criar Solicitação de Recurso",
  },
  components: { PartySelector, MemberSelector },
  data() {
    return {
      types,
      loading: false,
      error: null,
      party: null,
      itens: [],
      ownerType: "me",
      owner: null,
      activePanel: null,
    };
  },
  methods: {
    async createResourceRequest() {
      const { resourceRequest } = await RESOURCE_REQUEST.create({
        items: this.itens,
        partyId: this.party.id,
        ownerId: this.ownerType === "me" ? undefined : this.owner.id,
      });
      return resourceRequest;
    },
    async saveDraft() {
      this.loading = "draft";
      this.error = null;
      try {
        const resourceRequest = await this.createResourceRequest();
        this.$router.push(`/admin/resource-request/${resourceRequest.id}`);
      } catch (e) {
        this.error = e.message || "Erro ao salvar solicitação de recurso";
      } finally {
        this.loading = false;
      }
    },
    addItem(item) {
      let data = types[item].defaultData && types[item].defaultData();
      this.itens.push({
        type: item,
        valid: false,
        amount: 0,
        data: data || {},
      });
      this.activePanel = this.itens.length - 1;
    },
    removeItem(key) {
      const item = this.itens[key];
      const type = types[item.type];
      const confirm = window.confirm(
        "Tem certeza que deseja remover item do tipo " + type.name + "?"
      );
      if (confirm) this.itens.splice(key, 1);
    },
    updateItem(key, data) {
      this.itens[key].data = data;
    },
    updateValid(key, valid) {
      this.itens[key].valid = valid;
    },
    updateTotal(key, amount) {
      this.itens[key].amount = amount;
    },
  },
  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    ...mapGetters("company", ["selectedCompany"]),
    total() {
      return this.itens.reduce((acc, item) => acc + item.amount || 0, 0);
    },
    isValid() {
      if (!this.party) return false;
      if (this.itens.length === 0) return false;
      if (this.ownerType === "third" && !this.owner) return false;
      return true;
    },
  },
};
</script>
